import { Phone, PhoneIncoming, PhoneOutgoing, Users, ChevronRight, Plus, ListTodo, Mail, Edit2, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDateTimeAU } from '@/lib/utils';
import { useState } from 'react';

export default function CallLogCard({ 
  callLogs = [], 
  onViewAll, 
  onAddNew,
  onEdit,
  onDelete,
  onCreateTask,
  onSendSummary,
  maxDisplay = 3,
  showAddButton = true,
  showActions = true,
  title = 'Call Log'
}) {
  const [sendingId, setSendingId] = useState(null);
  const displayLogs = callLogs.slice(0, maxDisplay);

  const handleSendSummary = async (log) => {
    if (onSendSummary) {
      setSendingId(log.id);
      try {
        await onSendSummary(log);
      } finally {
        setSendingId(null);
      }
    }
  };

  const getCallTypeIcon = (type) => {
    switch (type) {
      case 'Incoming': return <PhoneIncoming className="w-4 h-4 text-[#6B8E6B]" />;
      case 'Outgoing': return <PhoneOutgoing className="w-4 h-4 text-[#4A3B32]" />;
      case 'Meeting': return <Users className="w-4 h-4 text-[#D99A6C]" />;
      default: return <Phone className="w-4 h-4" />;
    }
  };

  const getCallTypeStyle = (type) => {
    switch (type) {
      case 'Incoming': return 'bg-[#6B8E6B]/10';
      case 'Outgoing': return 'bg-[#4A3B32]/10';
      case 'Meeting': return 'bg-[#D99A6C]/10';
      default: return 'bg-[#F0EBE6]';
    }
  };

  return (
    <div className="caffeine-card p-6" data-testid="call-log-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-[#4A3B32]" />
          <h3 className="font-heading text-lg text-[#2C2420]">{title}</h3>
        </div>
        {showAddButton && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onAddNew}
            className="caffeine-btn-ghost"
            data-testid="add-call-log-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Log Call
          </Button>
        )}
      </div>

      {displayLogs.length === 0 ? (
        <div className="text-center py-6 text-[#8C8078]">
          <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No call logs yet</p>
          {showAddButton && (
            <Button 
              variant="link" 
              size="sm"
              onClick={onAddNew}
              className="text-[#4A3B32] mt-2"
            >
              Log your first call
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {displayLogs.map(log => (
            <div 
              key={log.id}
              className="p-3 rounded-lg bg-[#F9F7F5] hover:bg-[#F0EBE6] transition-colors"
              data-testid={`call-log-${log.id}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getCallTypeStyle(log.callType)}`}>
                  {getCallTypeIcon(log.callType)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-[#2C2420]">{log.callType}</span>
                    <span className="text-xs text-[#8C8078]">{formatDateTimeAU(log.dateTime)}</span>
                  </div>
                  <p className="text-sm text-[#5D524D] line-clamp-2">{log.summary}</p>
                  {log.participants && log.participants.length > 0 && (
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-xs text-[#8C8078]">With:</span>
                      <span className="text-xs text-[#4A3B32]">
                        {log.participants.map(p => p.name).join(', ')}
                      </span>
                    </div>
                  )}
                  {log.tags && log.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {log.tags.map(tag => (
                        <Badge 
                          key={tag} 
                          variant="secondary"
                          className="text-xs bg-[#E6E0D9] text-[#5D524D]"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Quick Action Buttons */}
                  {showActions && (
                    <div className="flex items-center gap-2 mt-3 pt-2 border-t border-[#E6E0D9]">
                      {onCreateTask && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); onCreateTask(log); }}
                          className="h-7 px-2 text-xs text-[#4A3B32] hover:bg-[#4A3B32]/10"
                          data-testid={`create-task-from-call-${log.id}`}
                        >
                          <ListTodo className="w-3 h-3 mr-1" />
                          Create Task
                        </Button>
                      )}
                      {onSendSummary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleSendSummary(log); }}
                          disabled={sendingId === log.id}
                          className="h-7 px-2 text-xs text-[#6B8E6B] hover:bg-[#6B8E6B]/10"
                          data-testid={`send-summary-${log.id}`}
                        >
                          {sendingId === log.id ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Mail className="w-3 h-3 mr-1" />
                          )}
                          Send Summary
                        </Button>
                      )}
                      {onEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); onEdit(log); }}
                          className="h-7 px-2 text-xs text-[#5D524D] hover:bg-[#4A3B32]/10"
                          data-testid={`edit-call-${log.id}`}
                        >
                          <Edit2 className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); onDelete(log); }}
                          className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                          data-testid={`delete-call-${log.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Delete
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {callLogs.length > maxDisplay && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onViewAll}
          className="w-full mt-4 caffeine-btn-ghost"
          data-testid="view-all-calls-btn"
        >
          View all call history in Activity
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
      
      {callLogs.length === 1 && maxDisplay === 1 && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onViewAll}
          className="w-full mt-4 caffeine-btn-ghost"
          data-testid="view-call-history-btn"
        >
          View call history in Activity
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}
